// Standard Library Imports
import React, { useEffect, useState, useContext, useRef } from 'react';
//External library imports
import { toast } from 'react-toastify';
import { UilImport, UilInfoCircle } from '@iconscout/react-unicons';
import _ from 'lodash';
import moment from 'moment';
//Internal module imports
import { useLoader, useQueryParamWithDefault } from '../../../hooks';
import { AdminService } from '../../../services/BiddingService/CommonServices/AdminService';
import { CustomerService } from '../../../services/BiddingService/CommonServices/CustomerService';
import { LicenseTypeService } from '../../../services/BiddingService/CommonServices/LicenseType';
import { InstanceService } from '../../../services/BiddingService/CommonServices/InstanceService';
import economicsClasses from './Economics.module.css';
import DateRangePicker from '../../../components/Inputs/DateRangePicker/DateRangePicker';
import { DropdownComponent } from '../../../components/Inputs/Input';
import Typography from '../../../components/Typography/Typography';
import CustomTooltip from '../../../components/CustomToolTip/CustomTooltip';
import Table from '../../../components/Table/Table';
import { RootEconomicsService } from '../../../services/BiddingService/Root/EconomicsService';
import { momentTimeFormater } from '../../../utils/timeHelper';
import { dkCurrencyFormat } from '../../../utils/currencyHelper';
import { Capitalize } from '../../../utils/stringHelper';
import { DownloadAsExcel } from '../../../utils/downloadAsExcel';
import { AuthContext } from '../../../context/AuthContext';
import { ROOT_USER_ROLES, ADMIN_USER_ROLES, ENABLER_USER_ROLES } from '../../../constants';
import { AdminEconomicsService } from '../../../services/BiddingService/Admin/EconomicsService';
import ModalComponent from '../../../components/ModalComponent/ModalComponent';
import BiddingInfo from '../Bidding/ModalComponent/BidInfo';
import { EnablerEconomicsService } from '../../../services/BiddingService/Enabler/EconomicsService';
import { DateParam, StringParam } from 'use-query-params';

const getDropdownOption = (data = []) => {
    const temp = [
        {
            label: 'All',
            value: 'all',
        },
    ];
    data.map((el) => {
        temp.push({ label: el.name, value: el._id });
    });
    return temp;
};

const DEFAULT_DROPDOWN_OPTION = {
    label: 'All',
    value: 'all',
};

const TABLE_HEADERS_KEYS = {
    root: {
        headers: [
            'Date',
            'Timeslot',
            'Partner',
            'Customer',
            'Instance',
            'Capacity (MW)',
            'Total CM/EAM (€)',
            'SC CM/EAM (€)',
            'Partner CM/EAM (€)',
            'Customer CM/EAM (€)',
            'Enablers CM/EAM (€)',
            'Type',
        ],
        keys: ['date', 'timeSlot', 'admin', 'customer', 'instance', 'capacity', 'totalRev', 'scRevenue', 'partnerRevenue', 'customerRevenue', 'enablersRevenue', 'type'],
    },
    admin: {
        headers: ['Date', 'Timeslot', 'Customer', 'Instance', 'Capacity (MW)', 'Product Name', 'Product Type', 'Revenue (€)', 'Energy Revenue (€)', 'Type', 'Info'],
        keys: ['date', 'timeSlot', 'customer', 'instance', 'capacity', 'productName', 'productType', 'partnerRevenue', 'energyRevenue', 'type', 'action'],
    },
    enabler: {
        headers: ['Date', 'Timeslot', 'Customer', 'Instance', 'Capacity (MW)', 'Product Name', 'Revenue (€)', 'Energy Revenue (€)', 'Type', 'Info'],
        keys: ['date', 'timeSlot', 'customer', 'instance', 'capacity', 'productName', 'enablersRevenue', 'energyRevenue', 'type', 'action'],
    },
};

const GRANULARITY = [
    { label: 'Days', value: 'days' },
    { label: 'Hours', value: 'hours' },
];

const setDropdownValuesDefault = (setOption, setSelected) => {
    setOption([DEFAULT_DROPDOWN_OPTION]);
    setSelected(DEFAULT_DROPDOWN_OPTION.value);
};

const Overview = ({ cId }) => {
    const [initialLoad, setInitialLoad] = useState(false);
    const { state } = useContext(AuthContext);
    const userRole = _.get(state, ['user', 'userRole']);
    const adminId = _.get(state, ['user', 'adminId']);
    const isRootUser = ROOT_USER_ROLES.includes(userRole);
    const isAdminUser = ADMIN_USER_ROLES.includes(userRole);
    const isEnablerUser = ENABLER_USER_ROLES.includes(userRole);
    const [startLoader, stopLoader] = useLoader();
    const [startDate, setStartDate] = useQueryParamWithDefault('startDate', DateParam, moment().startOf('month').toDate(), cId);
    const [endDate, setEndDate] = useQueryParamWithDefault('endDate', DateParam, moment().endOf('month').toDate(), cId);
    const [admins, setAdmins] = useState([DEFAULT_DROPDOWN_OPTION]);
    const [selectedAdmin, setSelectedAdmin] = useQueryParamWithDefault('admin', StringParam, isAdminUser ? adminId : DEFAULT_DROPDOWN_OPTION.value, cId);
    const [customers, setCustomers] = useState([DEFAULT_DROPDOWN_OPTION]);
    const [selectedCustomer, setSelectedCustomer] = useQueryParamWithDefault('customer', StringParam, DEFAULT_DROPDOWN_OPTION.value, cId);
    const [licenseTypes, setLicenseTypes] = useState([DEFAULT_DROPDOWN_OPTION]);
    const [selectedLicenseType, setSelectedLicenseType] = useQueryParamWithDefault('license', StringParam, DEFAULT_DROPDOWN_OPTION.value, cId);
    const [instances, setInstances] = useState([DEFAULT_DROPDOWN_OPTION]);
    const [selectedInstance, setSelectedInstance] = useQueryParamWithDefault('insatance', StringParam, DEFAULT_DROPDOWN_OPTION.value, cId);
    const [selectedGranularity, setSelectedGranularity] = useQueryParamWithDefault('granularity', StringParam, GRANULARITY[0].value, cId);
    const [economicsData, setEconomicsData] = useState({
        totalRevenue: {
            revenue: 0,
            sc: 0,
            customer: 0,
            partner: 0,
            enabler: 0,

            energy: 0,
            scEnergy: 0,
            partnerEnergy: 0,
            enablerEnergy: 0,
            customerEnergy: 0,
            customerEnergyBeforeDhCost: 0,
        },
        bidsData: [],
        totalBidsCount: 0,
    });
    const [paginationQuery, setPaginationQuery] = useState({
        skip: 0,
        page: 0,
        limit: selectedGranularity === 'hours' ? 1 : 15,
    });
    const [tableHeaderAndKey, setTableHeaderAndKey] = useState(
        isRootUser ? TABLE_HEADERS_KEYS.root : isAdminUser ? TABLE_HEADERS_KEYS.admin : isEnablerUser ? TABLE_HEADERS_KEYS.enabler : {}
    );
    const [bidInfoModal, setBidInfoModal] = useState({
        status: false,
        data: {},
    });

    const [fetchData, setFetchData] = useState(false);

    useEffect(() => {
        if (document.readyState === 'complete') setInitialLoad(true);
    }, []);

    useEffect(() => {
        if (isRootUser) getAdmins();
    }, []);

    useEffect(() => {
        if ((selectedAdmin && selectedAdmin !== 'all') || isEnablerUser) getCustomers();
        else if (selectedAdmin === 'all') {
            setDropdownValuesDefault(setCustomers, setSelectedCustomer);
            setDropdownValuesDefault(setLicenseTypes, setSelectedLicenseType);
            setDropdownValuesDefault(setInstances, setSelectedInstance);
        }
        setFetchData(true);
    }, [selectedAdmin]);

    useEffect(() => {
        if (isRootUser && selectedCustomer && selectedCustomer !== 'all') getLicenseType();
        else if (selectedCustomer === 'all') {
            setDropdownValuesDefault(setLicenseTypes, setSelectedLicenseType);
            setDropdownValuesDefault(setInstances, setSelectedInstance);
        }
        setFetchData(true);
    }, [selectedCustomer]);

    useEffect(() => {
        if (isRootUser && selectedLicenseType && selectedLicenseType !== 'all') getInstances();
        else if (selectedLicenseType === 'all') setDropdownValuesDefault(setInstances, setSelectedInstance);
        setFetchData(true);
    }, [selectedLicenseType]);

    useEffect(() => {
        setFetchData(true);
    }, [selectedInstance]);

    useEffect(() => {
        if (fetchData) getEconomicsData(true);
    }, [fetchData]);

    const getAdmins = () => {
        AdminService.GetAdmins(startLoader, handleGetAdminSuccess, handleError, stopLoader);
    };

    const handleGetAdminSuccess = ({ data }) => {
        const options = getDropdownOption(data.data);
        if (!selectedAdmin) setSelectedAdmin(options[0].value);
        setAdmins(options);
    };

    const getCustomers = () => {
        const params = isEnablerUser ? { enablerCustomers: true } : { adminId: selectedAdmin };
        CustomerService.GetCustomers(params, startLoader, handleGetCustomerSuccess, handleError, stopLoader);
    };

    const handleGetCustomerSuccess = ({ data }) => {
        const options = getDropdownOption(data.data);
        if (!selectedCustomer || initialLoad) setSelectedCustomer(options[0].value);
        setCustomers(options);
    };

    const getLicenseType = () => {
        LicenseTypeService.GetLicenseForCustomer(selectedCustomer, startLoader, handleGetLicenseTypeSuccess, handleError, stopLoader);
    };

    const handleGetLicenseTypeSuccess = ({ data }) => {
        const options = getDropdownOption(data.data);
        if (!selectedLicenseType || initialLoad) setSelectedLicenseType(options[0].value);
        setLicenseTypes(options);
    };

    const getInstances = () => {
        const params = {
            customerId: selectedCustomer,
            licenseId: selectedLicenseType,
        };
        InstanceService.GetInstances(params, startLoader, handleGetInstanceSuccess, handleError, stopLoader);
    };

    const handleGetInstanceSuccess = ({ data }) => {
        const options = getDropdownOption(data.data);
        if (!selectedInstance || initialLoad) setSelectedInstance(options[0].value);
        setInstances(options);
    };

    const getEconomicsData = (paginatedData = true, handler = null) => {
        const successHandler = handler ? handler : handleEconomicsSuccess;
        const params = {
            startDate: moment(startDate).format('YYYY-MM-DD'),
            endDate: moment(endDate).format('YYYY-MM-DD'),
            customerId: selectedCustomer,
            ...(isRootUser ? { adminId: selectedAdmin, instanceId: selectedInstance, licenseId: selectedLicenseType } : {}),
            granularity: selectedGranularity,
            ...(paginatedData ? { limit: paginationQuery.limit, skip: paginationQuery.skip } : {}),
        };

        let service;
        if (isRootUser) service = RootEconomicsService;
        if (isAdminUser) service = AdminEconomicsService;
        if (isEnablerUser) service = EnablerEconomicsService;

        service.GetEconomics(params, startLoader, successHandler, handleError, stopLoader);
    };

    const handleNull = (value) => (value || value === 0 ? (!isNaN(value) ? dkCurrencyFormat(value) : value) : '--');

    const handleEconomicsSuccess = ({ data }) => {
        const processedData = _.get(data, ['data', 'formatedData']).map((item) => ({
            ...item,
            date: momentTimeFormater(item.date).format('DD-MM-YYYY'),
            capacity: handleNull(item.capacity),
            ...(isRootUser
                ? {
                      totalRev: `${handleNull(item.revenue)} / ${handleNull(item.energyRevenue)}`,
                      scRevenue: `${handleNull(item.scRevenue)} / ${handleNull(item.scEnergyRevenue)}`,
                      partnerRevenue: `${handleNull(item.partnerRevenue)} / ${handleNull(item.partnerEnergyRevenue)}`,
                      customerRevenue: `${handleNull(item.customerRevenue)} / ${handleNull(item.customerEnergyRevenue)}`,
                      enablersRevenue: `${handleNull(item.enablersRevenue)} / ${handleNull(item.enablersEnergyRevenue)}`,
                  }
                : {}),

            ...(isAdminUser ? { productName: item.productName || '--', productType: item?.productType || '--', partnerRevenue: handleNull(item.partnerRevenue) } : {}),
            ...(isEnablerUser ? { productName: item.productName || '--', enablersRevenue: handleNull(item.enablersRevenue) } : {}),
            ...(isAdminUser || isEnablerUser ? { energyRevenue: !isNaN(item.energyRevenue) ? handleNull(item.energyRevenue) : '--' } : {}),
            type: Capitalize(item.type),
            ...(item?.modalData !== null
                ? {
                      action: (
                          <CustomTooltip content={'Bidding Info'}>
                              <div
                                  className={economicsClasses.Center}
                                  onClick={() => {
                                      setBidInfoModal({
                                          status: true,
                                          data: {
                                              capacity: {
                                                  overall: item.modalData.capacity,
                                                  panicHours: item.modalData.panicHours,
                                              },
                                              price: {
                                                  overall: item.modalData.price,
                                                  regulationPrice: item.modalData.regulationPrice || [],
                                              },
                                              settlement: {
                                                  bidStatus: item.modalData.bidStatus,
                                                  energyStatus: item.modalData?.energyStatus,
                                              },
                                              bidDate: item.date,
                                          },
                                      });
                                  }}
                              >
                                  <div>
                                      <UilInfoCircle size={'1.2vw'} style={{ color: 'var(--color-primary)' }} />
                                  </div>
                              </div>
                          </CustomTooltip>
                      ),
                  }
                : { action: '--' }),
        }));

        for (const key in _.get(data, ['data', 'totalRevenue'])) {
            _.set(data, ['data', 'totalRevenue', key], handleNull(Math.round(_.get(data, ['data', 'totalRevenue', key]))));
        }

        setEconomicsData({
            totalRevenue: _.get(data, ['data', 'totalRevenue']),
            totalBidsCount: _.get(data, ['data', 'count'], 0),
            bidsData: processedData || [],
        });
        setFetchData(false);
    };

    const handleEconomicsExportSuccess = ({ data }) => {
        const processedData = _.get(data, ['data', 'formatedData']).map((item) => ({
            date: momentTimeFormater(item.date).format('DD-MM-YYYY'),
            slot: item.timeSlot || '--',
            ...(isRootUser ? { admin: item.admin } : {}),
            customer: item.customer,
            instance: item.instance,
            capacity: item.capacity,
            ...(isRootUser
                ? {
                      revenue: item.revenue,
                      scRevenue: item.scRevenue,
                      partnerRevenue: item.partnerRevenue,
                      customerRevenue: item.customerRevenue,
                      enablersRevenue: item.enablersRevenue,

                      energyRevenue: item.energyRevenue,
                      scEnergyRevenue: item.scEnergyRevenue,
                      partnerEnergyRevenue: item.partnerEnergyRevenue,
                      customerEnergyRevenue: item.customerEnergyRevenue,
                      customerEnergyRevenueBeforeDhCost: item.customerEnergyRevenueBeforeDhCost,
                      enablersEnergyRevenue: item.enablersEnergyRevenue,
                  }
                : {}),
            ...(isAdminUser ? { productName: item.productName, productType: item?.productType || '--', partnerRevenue: item.partnerRevenue } : {}),
            ...(isEnablerUser ? { productName: item.productName, enablersRevenue: item.enablersRevenue } : {}),
            ...(isAdminUser || isEnablerUser ? { energyRevenue: !isNaN(item.energyRevenue) ? item.energyRevenue : '--' } : {}),
            type: item.type,
        }));

        const totalRevenue = {};
        for (const key in _.get(data, ['data', 'totalRevenue'])) {
            _.set(totalRevenue, [key], Math.round(_.get(data, ['data', 'totalRevenue', key])));
        }

        processedData.push({
            instance: 'Total (Euro)',
            capacity: '',
            ...(isRootUser
                ? {
                      revenue: totalRevenue.revenue,
                      scRevenue: totalRevenue.sc,
                      partnerRevenue: totalRevenue.partner,
                      customerRevenue: totalRevenue.customer,
                      enablersRevenue: totalRevenue.enabler,

                      energyRevenue: totalRevenue.energy,
                      scEnergyRevenue: totalRevenue.scEnergy,
                      partnerEnergyRevenue: totalRevenue.partnerEnergy,
                      customerEnergyRevenue: totalRevenue.customerEnergy,
                      customerEnergyRevenueBeforeDhCost: totalRevenue.customerEnergyRevenueBeforeDhCost,
                      enablersEnergyRevenue: totalRevenue.enablersEnergy,
                  }
                : {}),
            ...(isAdminUser ? { partnerRevenue: totalRevenue.revenue, energyRevenue: totalRevenue.energy } : {}),
            ...(isEnablerUser ? { enablersRevenue: totalRevenue.enabler, energyRevenue: totalRevenue.energy } : {}),
        });

        const headers = isRootUser
            ? [
                  'Date',
                  'Slot',
                  'Partner',
                  'Customer',
                  'Instance',
                  'Capacity (MW)',
                  'TOTAL CM (€)',
                  'SC CM (€)',
                  'PARTNER CM (€)',
                  'CUSTOMER CM (€)',
                  'ENABLER CM (€)',
                  'TOTAL EAM (€)',
                  'SC EAM (€)',
                  'PARTNER EAM (€)',
                  'CUSTOMER EAM (€)',
                  'Customer EAM (After Cost) (€)',
                  'ENABLER EAM (€)',
                  'Type',
              ]
            : tableHeaderAndKey.headers;

        if (processedData.length) {
            DownloadAsExcel(processedData, `Economics ${moment(startDate).format('YYYY-MM-DD')}-${moment(endDate).format('YYYY-MM-DD')}`, headers);
        } else {
            toast.error('No Data');
        }
    };

    const handlePageChange = (pageNo) => {
        setPaginationQuery((prev) => ({
            ...prev,
            skip: prev.limit * pageNo,
            page: pageNo,
        }));
        setFetchData(true);
    };

    const handleError = (err) => {
        toast.error(err?.response?.data?.message);
    };

    return (
        <div>
            <div className={economicsClasses.FilterBar}>
                <div className={economicsClasses.FilterContainer}>
                    {/* left container */}
                    <div className={economicsClasses.SubContainer}>
                        {isRootUser ? (
                            <>
                                <div className={economicsClasses.DropDownContainer}>
                                    <label>Admin</label>
                                    <DropdownComponent
                                        name="adminId"
                                        options={admins}
                                        defaultValue={{ value: selectedAdmin }}
                                        onChange={(e) => {
                                            if (e.value !== selectedAdmin?.value) setSelectedAdmin(e.value);
                                        }}
                                    />
                                </div>
                            </>
                        ) : (
                            ''
                        )}

                        <div className={economicsClasses.DropDownContainer}>
                            <label>Customer</label>
                            <DropdownComponent
                                name="customerId"
                                options={customers}
                                defaultValue={{ value: selectedCustomer }}
                                onChange={(e) => {
                                    if (e.value !== selectedCustomer) setSelectedCustomer(e.value);
                                }}
                            />
                        </div>

                        {isRootUser ? (
                            <>
                                <div className={economicsClasses.DropDownContainer}>
                                    <label>License</label>
                                    <DropdownComponent
                                        name="license"
                                        options={licenseTypes}
                                        defaultValue={{ value: selectedLicenseType }}
                                        onChange={(e) => {
                                            if (e.value !== selectedLicenseType) setSelectedLicenseType(e.value);
                                        }}
                                    />
                                </div>

                                <div className={economicsClasses.DropDownContainer}>
                                    <label>Instance</label>
                                    <DropdownComponent
                                        name="instance"
                                        options={instances}
                                        defaultValue={{ value: selectedInstance }}
                                        onChange={(e) => {
                                            if (e.value !== selectedInstance) setSelectedInstance(e.value);
                                        }}
                                    />
                                </div>
                            </>
                        ) : (
                            ''
                        )}
                    </div>

                    {/* right container */}
                    <div className={economicsClasses.SubContainer}>
                        <div className={economicsClasses.DropDownContainer}>
                            <label>Granularity</label>
                            <DropdownComponent
                                name="granularity"
                                options={GRANULARITY}
                                defaultValue={{ value: selectedGranularity }}
                                onChange={(e) => {
                                    if (e.value !== selectedGranularity) {
                                        if (e.value === 'hours') setPaginationQuery({ ...paginationQuery, limit: 1 });
                                        else setPaginationQuery({ ...paginationQuery, limit: 15 });
                                        setSelectedGranularity(e.value);
                                    }
                                }}
                            />
                        </div>

                        {startDate && endDate && (
                            <div>
                                <DateRangePicker
                                    startDate={startDate}
                                    endDate={endDate}
                                    onChange={(date) => {
                                        setStartDate(moment(date.startDate).toDate());
                                        setEndDate(moment(date.endDate).toDate());
                                    }}
                                    style={{ height: '2.5vw', marginBottom: '-0.9vh' }}
                                    showLabels={true}
                                />
                            </div>
                        )}

                        <div
                            style={{ marginTop: '0.8vh' }}
                            onClick={() => {
                                getEconomicsData(true);
                                // setPage(0);
                            }}
                        >
                            <button className="btn-primary">Submit</button>
                        </div>

                        <div onClick={() => getEconomicsData(false, handleEconomicsExportSuccess)} style={{ marginTop: '2vh' }}>
                            <UilImport
                                size="1.5vw"
                                style={{
                                    cursor: 'pointer',
                                    marginTop: '0.4vw',
                                    color: 'var(--color-primary)',
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className={economicsClasses.TableContainer}>
                <div className={economicsClasses.FilterContainer} style={{ marginTop: '1vh' }}>
                    <div className={economicsClasses.SubContainer}>
                        <div className={economicsClasses.TableCount}>
                            Total Revenue CM :
                            <span>
                                <Typography size="14" content={economicsData.totalRevenue.revenue + ' €'} />
                            </span>
                        </div>

                        {(isAdminUser || isEnablerUser) && (
                            <>
                                <div className={economicsClasses.TableCount}>
                                    Total Revenue EAM :
                                    <span>
                                        <Typography size="14" content={economicsData.totalRevenue.energy + ' €'} />
                                    </span>
                                </div>
                            </>
                        )}

                        {isRootUser ? (
                            <>
                                <div className={economicsClasses.TableCount}>
                                    Total Revenue CM (SC) :
                                    <span>
                                        <Typography size="14" content={economicsData.totalRevenue.sc + ' €'} />
                                    </span>
                                </div>

                                <div className={economicsClasses.TableCount}>
                                    Total Revenue CM : (Partner)
                                    <span>
                                        <Typography size="14" content={economicsData.totalRevenue.partner + ' €'} />
                                    </span>
                                </div>

                                <div className={economicsClasses.TableCount}>
                                    Total Revenue CM: (Customer)
                                    <span>
                                        <Typography size="14" content={economicsData.totalRevenue.customer + ' €'} />
                                    </span>
                                </div>

                                <div className={economicsClasses.TableCount}>
                                    Total Revenue CM : (Enablers)
                                    <span>
                                        <Typography size="14" content={economicsData.totalRevenue.enabler + ' €'} />
                                    </span>
                                </div>
                            </>
                        ) : isEnablerUser ? (
                            <>
                                <div className={economicsClasses.TableCount}>
                                    Total Revenue : ({_.get(state, ['user', 'name'])})
                                    <span>
                                        <Typography size="14" content={economicsData.totalRevenue.enabler + ' €'} />
                                    </span>
                                </div>
                            </>
                        ) : (
                            ''
                        )}

                        <div className={economicsClasses.TableCount}>
                            Total Bids Count :
                            <span>
                                <Typography size="14" content={economicsData.totalBidsCount || 0} />
                            </span>
                        </div>
                    </div>
                </div>
                {isRootUser && (
                    <div className={economicsClasses.FilterContainer} style={{ marginTop: '1vh' }}>
                        <div className={economicsClasses.SubContainer}>
                            <div className={economicsClasses.TableCount}>
                                Total Revenue EAM :
                                <span>
                                    <Typography size="14" content={economicsData.totalRevenue.energy + ' €'} />
                                </span>
                            </div>

                            <div className={economicsClasses.TableCount}>
                                Total Revenue EAM (SC) :
                                <span>
                                    <Typography size="14" content={economicsData.totalRevenue.scEnergy + ' €'} />
                                </span>
                            </div>

                            <div className={economicsClasses.TableCount}>
                                Total Revenue EAM : (Partner)
                                <span>
                                    <Typography size="14" content={economicsData.totalRevenue.partnerEnergy + ' €'} />
                                </span>
                            </div>

                            <div className={economicsClasses.TableCount}>
                                Total Revenue EAM: (Customer)
                                <span>
                                    <Typography size="14" content={economicsData.totalRevenue.customerEnergy + ' €'} />
                                </span>
                            </div>

                            <div className={economicsClasses.TableCount}>
                                Total Revenue EAM : (Enablers)
                                <span>
                                    <Typography size="14" content={economicsData.totalRevenue.enablerEnergy + ' €'} />
                                </span>
                            </div>
                        </div>
                    </div>
                )}
                <Table
                    head={tableHeaderAndKey.headers}
                    keys={tableHeaderAndKey.keys}
                    data={economicsData.bidsData}
                    page={paginationQuery.page}
                    Pagination={true}
                    limit={paginationQuery.limit}
                    handlePageChange={handlePageChange}
                    totalCount={economicsData.totalBidsCount}
                />

                {bidInfoModal.status && (
                    <ModalComponent
                        isOpen={bidInfoModal.status}
                        setOpen={() => {
                            setBidInfoModal({ status: false, data: {} });
                        }}
                    >
                        <BiddingInfo data={bidInfoModal.data} />
                    </ModalComponent>
                )}
            </div>
        </div>
    );
};

export default Overview;
